<template>
  <div v-if="nextVaccine" class="mb-6">
    <div
      @click="scrollToVaccineCard"
      class="bg-gradient-to-br from-primary to-secondary rounded-2xl p-5 shadow-lg relative overflow-hidden cursor-pointer active:scale-[0.98] transition-transform bounce-card"
    >
      <!-- 裝飾圖案 -->
      <div class="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
      <div class="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
      
      <div class="relative z-10">
  <div class="flex items-center gap-4 mb-3">
    <!-- 標題 -->
    <div class="flex items-center gap-2">
      <span class="text-xl">📅</span>
      <h3 class="text-white font-bold text-sm">下一次接種</h3>
    </div>
          <!-- 倒數天數 (右側) -->
          <div class="flex items-center gap-2">
  <span class="text-white/80 text-xs">距離</span>
  <div class="flex items-center gap-1 bg-white/15 rounded-full px-2 py-0.5">
    <span class="text-white text-3xl font-bold leading-none">
      {{ daysUntil }}
    </span>
    <span class="text-white text-xs">天</span>
  </div>
</div>

        </div>

        <!-- 疫苗資訊 -->
        <div class="bg-white/20 backdrop-blur-sm rounded-xl p-4">
            <!-- 疫苗名稱 -->
            <div class="flex items-start justify-between gap-3 mb-1">
              <p class="text-white font-bold text-base min-w-0">
                {{ nextVaccine.vaccineName }}
              </p>
              <VaccineTypeBadge :type="nextVaccine.type" />
            </div>

            <!-- 劑次與年齡（次要資訊） -->
            <p class="text-white/80 text-xs mb-3">
                {{ nextVaccine.doseLabel }} · {{ nextVaccine.ageLabel }}
            </p>

            <!-- 推薦日期區塊（視覺重點） -->
            <div class="flex items-center justify-between bg-white/15 rounded-lg px-3 py-2">
                <span class="text-white/80 text-xs">
                {{ nextVaccine.customPlannedDate ? '預計接種日期' : '推薦接種日期' }}
                </span>
                <span class="text-white font-bold text-base font-mono">
                {{ formatDate(nextVaccine.displayPlannedDate) }}
                </span>
            </div>
            </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { VaccinationRecord } from '../types';
import { getVaccineDetails, getDoseDetails } from '../services/scheduleService';
import { differenceInDays, parseISO } from 'date-fns';
import { getDisplayPlannedDate } from '../utils/vaccineDate';
import VaccineTypeBadge from './VaccineTypeBadge.vue';

interface Props {
  records: VaccinationRecord[];
}

const props = defineProps<Props>();

/**
 * 找出下一個未接種的疫苗（離今天最近的未來日期）
 */
const nextVaccine = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // 篩選未完成且未跳過的紀錄，且只選擇未來的日期（>= 今天）
  const upcomingRecords = props.records.filter(r => {
    if (r.status !== 'planned' && r.status !== 'overdue') return false;

    const displayDate = parseISO(getDisplayPlannedDate(r));
    displayDate.setHours(0, 0, 0, 0);

    // 只保留今天或未來的日期
    return displayDate >= today;
  });

  if (upcomingRecords.length === 0) return null;

  // 排序：最近的未來日期優先
  const sorted = upcomingRecords.sort((a, b) => {
    const dateA = parseISO(getDisplayPlannedDate(a));
    const dateB = parseISO(getDisplayPlannedDate(b));
    return dateA.getTime() - dateB.getTime();
  });

  const record = sorted[0];
  if (!record) return null;

  const vaccine = getVaccineDetails(record.vaccineId);
  const dose = getDoseDetails(record.doseId);

  const displayDate = getDisplayPlannedDate(record);

  return {
    ...record,
    displayPlannedDate: displayDate,
    vaccineName: vaccine?.name || '未知疫苗',
    type: dose?.type ?? vaccine?.type,
    doseLabel: dose?.label || '',
    ageLabel: dose?.customAgeLabel || ''
  };
});

/**
 * 計算距離天數
 */
const daysUntil = computed(() => {
  if (!nextVaccine.value) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const targetDate = parseISO(nextVaccine.value.displayPlannedDate);
  const days = differenceInDays(targetDate, today);

  // 如果是負數（已過期），顯示為 0
  return days < 0 ? 0 : days;
});

/**
 * 格式化日期顯示
 */
const formatDate = (dateStr: string): string => {
  return dateStr.replace(/-/g, '/');
};

/**
 * 滾動到對應的疫苗卡片
 */
const scrollToVaccineCard = () => {
  if (!nextVaccine.value) return;
  
  // 使用疫苗紀錄的 ID 找到對應的 DOM 元素
  const cardElement = document.getElementById(`vaccine-card-${nextVaccine.value.id}`);
  
  if (cardElement) {
    // 滾動到該元素，並留一些上方空間
    cardElement.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center'
    });
    
    // 添加高亮效果
    cardElement.classList.add('highlight-card');
    setTimeout(() => {
      cardElement.classList.remove('highlight-card');
    }, 2000);
  }
};
</script>

<style scoped>
@keyframes gentle-bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}

.bounce-card {
  animation: gentle-bounce 2s ease-in-out infinite;
}

.bounce-card:hover {
  animation-play-state: paused;
}
</style>
